import { useEffect, useRef, useCallback } from 'react';

export interface RecentInteraction {
  type: string;
  target?: string;
  timestamp: number;
}

const MAX_INTERACTIONS = 20;

function describeElement(element: Element | null): string {
  if (!element) return 'unknown';
  const tag = element.tagName.toLowerCase();
  const id = element.id ? `#${element.id}` : '';
  const testId = element.getAttribute('data-testid');
  const testIdPart = testId ? `[data-testid=${testId}]` : '';
  const className = element.className && typeof element.className === 'string'
    ? `.${element.className.split(' ').slice(0, 2).join('.')}`
    : '';
  const text = (element.textContent || '').trim().slice(0, 40);
  return `${tag}${id}${testIdPart}${className}${text ? ` "${text}"` : ''}`;
}

export function useRecentInteractions(): () => RecentInteraction[] {
  const interactionsRef = useRef<RecentInteraction[]>([]);

  const push = useCallback((interaction: RecentInteraction) => {
    interactionsRef.current.push(interaction);
    if (interactionsRef.current.length > MAX_INTERACTIONS) {
      interactionsRef.current = interactionsRef.current.slice(interactionsRef.current.length - MAX_INTERACTIONS);
    }
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      push({
        type: 'click',
        target: describeElement(e.target as Element | null),
        timestamp: Date.now(),
      });
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      push({
        type: 'keydown',
        target: e.key,
        timestamp: Date.now(),
      });
    };

    const handleError = (e: ErrorEvent) => {
      push({
        type: 'error',
        target: `${e.message} @ ${e.filename}:${e.lineno}`,
        timestamp: Date.now(),
      });
    };

    const handleUnhandledRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason instanceof Error ? e.reason.message : String(e.reason);
      push({
        type: 'unhandledrejection',
        target: reason,
        timestamp: Date.now(),
      });
    };

    const handleOnline = () => push({ type: 'online', timestamp: Date.now() });
    const handleOffline = () => push({ type: 'offline', timestamp: Date.now() });
    const handleVisibility = () =>
      push({ type: `visibility:${document.visibilityState}`, timestamp: Date.now() });

    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [push]);

  return useCallback(() => interactionsRef.current.slice(), []);
}
